/*
 * Um chamado: a conversa e a resposta.
 *
 * Quem atende ve tambem as notas internas (em amarelo, para nunca serem
 * confundidas com resposta) e muda situacao e prioridade. Quem abriu
 * responde e, se o chamado foi finalizado ha pouco, reabre respondendo.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  PERMISSIONS,
  TICKET_CATEGORY_LABELS,
  TICKET_PRIORITY_LABELS,
  TICKET_PRIORITY_VALUES,
  TICKET_STATUS_LABELS,
  TICKET_STATUS_VALUES,
  type TicketStatus,
} from '@hotspot/shared';
import { ApiRequestError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  INTERVALO_CONVERSA_MS,
  avisarMudancaNosChamados,
  useAtualizacaoPeriodica,
} from '../lib/tickets';
import type { TicketDetailDto, TicketMessageDto } from '../lib/types';
import { Alert, Button, Card, Spinner } from '../components/ui';
import {
  CAMPO,
  PrazoBadge,
  PrioridadeBadge,
  SeletorDeImagens,
  SituacaoBadge,
  duracao,
  enviarAnexos,
  quando,
  textoDoLocal,
} from '../components/TicketBits';

export function TicketPage() {
  const { id } = useParams();
  const location = useLocation();
  const { can } = useAuth();
  const atende = can(PERMISSIONS.TICKET_MANAGE);
  const ticketId = Number(id);

  const [chamado, setChamado] = useState<TicketDetailDto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(() => {
    const falhas = (location.state as { falhasDeAnexo?: string[] } | null)?.falhasDeAnexo;
    return falhas?.length ? `Algumas imagens não foram anexadas: ${falhas.join('; ')}` : null;
  });

  // O que ja esta na tela, para a consulta periodica so redesenhar se mudou.
  const naTela = useRef<string | null>(null);

  const carregar = useCallback(
    async (silencioso = false) => {
      try {
        const dados = await apiRequest<TicketDetailDto>(`/admin/tickets/${ticketId}`);
        const assinatura = JSON.stringify(dados);
        if (assinatura === naTela.current) return;

        naTela.current = assinatura;
        setChamado(dados);
        setErro(null);
        // Abrir marca as respostas como lidas: o numero do menu precisa saber.
        avisarMudancaNosChamados();
      } catch (caught) {
        // Falha na consulta periodica nao derruba a conversa que ja esta na tela.
        if (silencioso) return;
        setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar');
      }
    },
    [ticketId],
  );

  useEffect(() => {
    naTela.current = null;
    void carregar();
  }, [carregar]);

  useAtualizacaoPeriodica(() => void carregar(true), INTERVALO_CONVERSA_MS);

  async function alterar(mudanca: { status?: string; priority?: string }) {
    try {
      await apiRequest(`/admin/tickets/${ticketId}`, { method: 'PATCH', body: mudanca });
      await carregar();
    } catch (caught) {
      setAviso(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao alterar');
    }
  }

  if (erro) {
    return (
      <div className="space-y-4">
        <Voltar />
        <Alert>{erro}</Alert>
      </div>
    );
  }
  if (!chamado) return <Spinner label="Abrindo o chamado..." />;

  const encerrado = chamado.status === 'finalizado' || chamado.status === 'cancelado';
  const podeResponder = atende || !encerrado || chamado.podeReabrir;

  return (
    <div className="space-y-6">
      <Voltar />

      <header className="space-y-2">
        <h1 className="text-2xl font-bold text-slate-900">
          <span className="text-slate-400 tabular-nums">#{chamado.id}</span> {chamado.title}
        </h1>
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
          <SituacaoBadge status={chamado.status} />
          <PrioridadeBadge priority={chamado.priority} />
          <span>
            {TICKET_CATEGORY_LABELS[chamado.category as keyof typeof TICKET_CATEGORY_LABELS] ??
              chamado.category}
          </span>
          <span>·</span>
          <span>
            aberto por {chamado.createdBy?.name ?? 'usuário removido'} em{' '}
            {quando(chamado.createdAt)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PrazoBadge
            prazo={chamado.sla.resposta}
            pausado={chamado.sla.pausado}
            rotulo="1ª resposta"
          />
          <PrazoBadge prazo={chamado.sla.solucao} pausado={chamado.sla.pausado} rotulo="Solução" />
          {chamado.sla.reChamadoDe ? (
            <Link
              to={`/chamados/${chamado.sla.reChamadoDe}`}
              className="text-xs font-semibold text-amber-700 hover:underline"
              title="Falha no mesmo ponto pouco depois de resolvida: o prazo de solução é o que sobrou do anterior."
            >
              Re-chamado do #{chamado.sla.reChamadoDe} · {duracao(chamado.sla.herdadoSeg)} já
              consumidas
            </Link>
          ) : null}
        </div>
        {chamado.local ? (
          <p className="text-sm text-slate-700">
            <span className="font-medium">Praça:</span> {textoDoLocal(chamado.local)}
            {chamado.local.vlan ? (
              <span className="ml-1.5 text-xs text-slate-400">{chamado.local.vlan}</span>
            ) : null}
          </p>
        ) : null}
      </header>

      {aviso ? <Alert tone="warning">{aviso}</Alert> : null}

      {atende ? (
        <Card>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Situação
              <select
                value={chamado.status}
                onChange={(evento) => void alterar({ status: evento.target.value })}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm"
              >
                {TICKET_STATUS_VALUES.map((valor) => (
                  <option key={valor} value={valor}>
                    {TICKET_STATUS_LABELS[valor]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Prioridade
              <select
                value={chamado.priority}
                onChange={(evento) => void alterar({ priority: evento.target.value })}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm"
              >
                {TICKET_PRIORITY_VALUES.map((valor) => (
                  <option key={valor} value={valor}>
                    {TICKET_PRIORITY_LABELS[valor]}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </Card>
      ) : null}

      <ol className="space-y-3">
        {chamado.messages.map((mensagem) => (
          <Mensagem key={mensagem.id} mensagem={mensagem} />
        ))}
      </ol>

      {podeResponder ? (
        <Resposta
          ticketId={chamado.id}
          atende={atende}
          vaiReabrir={!atende && chamado.status === 'finalizado'}
          onEnviada={async (falhas) => {
            setAviso(
              falhas.length ? `Algumas imagens não foram anexadas: ${falhas.join('; ')}` : null,
            );
            await carregar();
          }}
        />
      ) : (
        <Alert tone="info">
          Este chamado foi {chamado.status === 'cancelado' ? 'cancelado' : 'finalizado'}. Se
          precisar de ajuda de novo,{' '}
          <Link to="/chamados" className="font-semibold underline">
            abra um novo chamado
          </Link>
          .
        </Alert>
      )}
    </div>
  );
}

function Voltar() {
  return (
    <Link to="/chamados" className="text-sm font-medium text-brand-700 hover:underline">
      ← Chamados
    </Link>
  );
}

function Mensagem({ mensagem }: { mensagem: TicketMessageDto }) {
  if (mensagem.kind === 'event') {
    return (
      <li className="text-center text-xs text-slate-500">
        {mensagem.body.replace(/^Situacao:/, 'Situação:')} · {mensagem.author?.name ?? 'sistema'} ·{' '}
        {quando(mensagem.createdAt)}
      </li>
    );
  }

  const nota = mensagem.kind === 'note';
  const estilo = nota
    ? 'bg-amber-50 ring-amber-200'
    : mensagem.doSuporte
      ? 'bg-brand-50 ring-brand-100'
      : 'bg-white ring-slate-200/70';

  return (
    <li className={`rounded-2xl p-4 shadow-card ring-1 ${estilo}`}>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2 text-xs text-slate-500">
        <span className="font-semibold text-slate-800">
          {mensagem.author?.name ?? 'Usuário removido'}
          {nota ? (
            <span className="ml-2 font-medium text-amber-800">
              Nota interna · quem abriu não vê
            </span>
          ) : mensagem.doSuporte ? (
            <span className="ml-2 font-medium text-brand-700">Suporte</span>
          ) : null}
        </span>
        <span>{quando(mensagem.createdAt)}</span>
      </div>
      <p className="text-sm whitespace-pre-wrap text-slate-800">{mensagem.body}</p>

      {mensagem.attachments.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {mensagem.attachments.map((anexo) => (
            <a
              key={anexo.id}
              href={`/api/admin/tickets/attachments/${anexo.id}`}
              target="_blank"
              rel="noreferrer"
              title={anexo.fileName}
            >
              <img
                src={`/api/admin/tickets/attachments/${anexo.id}`}
                alt={anexo.fileName}
                loading="lazy"
                className="h-28 max-w-56 rounded-lg object-cover ring-1 ring-slate-200 hover:ring-brand-400"
              />
            </a>
          ))}
        </div>
      ) : null}
    </li>
  );
}

function Resposta({
  ticketId,
  atende,
  vaiReabrir,
  onEnviada,
}: {
  ticketId: number;
  atende: boolean;
  vaiReabrir: boolean;
  onEnviada: (falhasDeAnexo: string[]) => Promise<void>;
}) {
  const [texto, setTexto] = useState('');
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [interna, setInterna] = useState(false);
  const [depois, setDepois] = useState<'' | TicketStatus>('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    setEnviando(true);
    setErro(null);

    try {
      const criada = await apiRequest<{ messageId: number }>(
        `/admin/tickets/${ticketId}/messages`,
        {
          method: 'POST',
          body: {
            body: texto,
            ...(interna ? { internal: true } : {}),
            ...(depois ? { status: depois } : {}),
          },
        },
      );
      const falhas = await enviarAnexos(ticketId, criada.messageId, arquivos);
      setTexto('');
      setArquivos([]);
      setInterna(false);
      setDepois('');
      avisarMudancaNosChamados();
      await onEnviada(falhas);
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao enviar');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Card title={interna ? 'Nota interna' : 'Responder'}>
      <form onSubmit={enviar} className="space-y-3">
        {erro ? <Alert>{erro}</Alert> : null}
        {vaiReabrir ? <Alert tone="info">Responder reabre este chamado.</Alert> : null}

        <textarea
          value={texto}
          onChange={(evento) => setTexto(evento.target.value)}
          rows={4}
          required
          maxLength={5000}
          disabled={enviando}
          placeholder={interna ? 'Só quem atende chamados vai ver.' : 'Escreva sua mensagem'}
          className={`${CAMPO} ${interna ? 'bg-amber-50' : ''}`}
        />

        <SeletorDeImagens arquivos={arquivos} onChange={setArquivos} disabled={enviando} />

        <div className="flex flex-wrap items-center justify-between gap-3">
          {atende ? (
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={interna}
                  onChange={(evento) => setInterna(evento.target.checked)}
                  className="size-4 rounded border-slate-300 text-amber-600"
                />
                Nota interna
              </label>
              <label className="flex items-center gap-2">
                Depois de enviar, marcar como
                <select
                  value={depois}
                  onChange={(evento) => setDepois(evento.target.value as '' | TicketStatus)}
                  className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm shadow-sm"
                >
                  <option value="">(manter)</option>
                  {TICKET_STATUS_VALUES.filter((valor) => valor !== 'aberto').map((valor) => (
                    <option key={valor} value={valor}>
                      {TICKET_STATUS_LABELS[valor]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : (
            <span />
          )}
          <Button type="submit" disabled={enviando || !texto.trim()}>
            {enviando ? 'Enviando...' : interna ? 'Salvar nota' : 'Enviar'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
